/**
 * Call-note candidates (A7Y-3872, D13): every Drive note HELM matched to an
 * account, with the match's own chip linking straight to that account's
 * pipeline section, and a note HELM could not match at all shown plainly
 * rather than silently dropped ("No account matched; log it with call
 * intake" — the procedure's own go-question, `call-intake.md:208`, "a state
 * mismatch is surfaced, not changed"). A note can carry more than one
 * account match (D13); every one gets its own chip.
 */
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import { HELM_PIPELINE_URL } from '@a7y/platform-health/helm';
import type { HelmActionInput, HelmDriveNote } from '@a7y/platform-health/helm';
import { ItemActionsMenu } from '../ItemActionsMenu';
import { HELM_TABLE_GUTTER, HelmEmpty, HelmSection } from './HelmSection';
import { HelmLink } from '../HelmLink';

export function CallIntake({
  notes,
  suppressedKeys,
  onAction,
}: {
  notes: HelmDriveNote[];
  suppressedKeys: ReadonlySet<string>;
  onAction: (input: HelmActionInput) => void;
}) {
  const visible = notes.filter((note) => !suppressedKeys.has(note.key));

  return (
    <HelmSection title="Call intake" lineOn="none">
      {visible.length === 0 ? (
        <HelmEmpty>none</HelmEmpty>
      ) : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader>Note</TableHeader>
              <TableHeader>Accounts</TableHeader>
              <TableHeader></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((note) => (
              <TableRow key={note.key} data-testid="helm-call-note">
                <TableCell className="min-w-0 break-words">
                  <a
                    href={note.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium hover:underline"
                  >
                    {note.meetingTitle ?? note.name}
                  </a>{' '}
                  <span className="text-xs text-zinc-500">
                    · {new Date(note.modifiedAt).toLocaleString()} ·{' '}
                    {note.ownedBy === 'yevhen' ? 'yours' : 'shared'}
                  </span>
                </TableCell>
                <TableCell className="min-w-0">
                  <div className="flex flex-wrap gap-1">
                    {note.accounts.length === 0 ? (
                      <span
                        data-testid="helm-call-unmatched"
                        className="text-xs text-amber-600 dark:text-amber-400"
                      >
                        No account matched; log it with call intake
                      </span>
                    ) : (
                      note.accounts.map((match) =>
                        match.anchor ? (
                          // A7Y-5210: the chip opens the account's page in the
                          // app (it used to open `pipeline.md` on GitHub). The
                          // testid stays on the link itself (the real
                          // interactive, href-bearing element).
                          <HelmLink
                            key={`${note.key}-${match.account}`}
                            href={`${HELM_PIPELINE_URL}#${match.anchor}`}
                            data-testid="helm-call-account-chip"
                            className="hover:underline"
                          >
                            <Badge color="zinc">{match.account}</Badge>
                          </HelmLink>
                        ) : (
                          <Badge
                            key={`${note.key}-${match.account}`}
                            data-testid="helm-call-account-chip"
                            color="zinc"
                          >
                            {match.account}
                          </Badge>
                        )
                      )
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <ItemActionsMenu
                    itemKey={note.key}
                    decision={note.decision}
                    onAction={onAction}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}
